'use client';

import { ArrowRight, Settings, Shield, Truck } from 'lucide-react';
import Link from 'next/link';
import AnimateIn from '@/components/ui/AnimateIn';
import { ScrollTracker } from '@/components/ui/ScrollTracker';

const About = () => {
    return (
        <section
            className="flex items-center justify-center bg-gray-100 py-16"
            style={{
                minHeight:
                    'calc(100vh - 80px - var(--announcement-offset, 0px))',
            }}
        >
            <ScrollTracker sectionName="about" threshold={0.3} />
            <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <AnimateIn className="text-center mb-12">
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                        New Zealand&rsquo;s Leading Shoring Provider
                    </h2>
                    <div className="w-24 h-1 bg-brand mx-auto mb-4" />
                    <p className="text-lg text-gray-600 max-w-[80%] mx-auto">
                        For over 25 years, Trenchmate has been revolutionising
                        construction safety and efficiency through innovative
                        shoring solutions. As New Zealand&rsquo;s only company
                        that custom-configures shoring shields, we offer a
                        modular system with over 140 variations to fit your
                        specific job requirements.
                    </p>
                </AnimateIn>

                <AnimateIn
                    delay={0.1}
                    className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12"
                >
                    <div className="text-center p-6 bg-white border border-gray-200 hover:border-brand/50 shadow-sm hover:shadow-md transition-all duration-300 group">
                        <div className="w-12 h-12 bg-gray-50 border border-gray-200 group-hover:bg-brand group-hover:border-brand flex items-center justify-center mx-auto mb-4 transition-colors duration-300">
                            <Shield className="w-6 h-6 text-brand group-hover:text-white transition-colors duration-300" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                            Custom Configurations
                        </h3>
                        <p className="text-gray-600">
                            Over 140 shield variations available through our
                            modular system, offering ultimate flexibility for
                            any project.
                        </p>
                    </div>

                    <div className="text-center p-6 bg-white border border-gray-200 hover:border-brand/50 shadow-sm hover:shadow-md transition-all duration-300 group">
                        <div className="w-12 h-12 bg-gray-50 border border-gray-200 group-hover:bg-brand group-hover:border-brand flex items-center justify-center mx-auto mb-4 transition-colors duration-300">
                            <Settings className="w-6 h-6 text-brand group-hover:text-white transition-colors duration-300" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                            Delivered Ready to Use
                        </h3>
                        <p className="text-gray-600">
                            All products are delivered and installed to site
                            fully assembled, ready to use.
                        </p>
                    </div>

                    <div className="text-center p-6 bg-white border border-gray-200 hover:border-brand/50 shadow-sm hover:shadow-md transition-all duration-300 group">
                        <div className="w-12 h-12 bg-gray-50 border border-gray-200 group-hover:bg-brand group-hover:border-brand flex items-center justify-center mx-auto mb-4 transition-colors duration-300">
                            <Truck className="w-6 h-6 text-brand group-hover:text-white transition-colors duration-300" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                            Nationwide Coverage & Delivery
                        </h3>
                        <p className="text-gray-600">
                            Dedicated delivery trucks providing efficient
                            service directly to construction sites across New
                            Zealand.
                        </p>
                    </div>
                </AnimateIn>

                <div className="text-center">
                    <AnimateIn
                        delay={0.2}
                        className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8"
                    >
                        <div className="bg-white border border-gray-200 hover:border-brand/40 p-6 text-left shadow-sm hover:shadow-md transition-all duration-300">
                            <h4 className="font-semibold text-gray-900 mb-3 text-lg">
                                Modular Design Benefits
                            </h4>
                            <ul className="text-gray-600 space-y-2">
                                <li className="flex items-start">
                                    <span className="text-brand mr-2">•</span>
                                    Faster installation and removal saves
                                    valuable time
                                </li>
                                <li className="flex items-start">
                                    <span className="text-brand mr-2">•</span>
                                    Reduced labor costs through efficient design
                                </li>
                                <li className="flex items-start">
                                    <span className="text-brand mr-2">•</span>
                                    Adaptable to changing site conditions and
                                    requirements
                                </li>
                            </ul>
                        </div>

                        <div className="bg-white border border-gray-200 hover:border-brand/40 p-6 text-left shadow-sm hover:shadow-md transition-all duration-300">
                            <h4 className="font-semibold text-gray-900 mb-3 text-lg">
                                Safety & Compliance
                            </h4>
                            <ul className="text-gray-600 space-y-2">
                                <li className="flex items-start">
                                    <span className="text-brand mr-2">•</span>
                                    Meets all New Zealand safety standards and
                                    regulations
                                </li>
                                <li className="flex items-start">
                                    <span className="text-brand mr-2">•</span>
                                    Comprehensive safety documentation provided
                                </li>
                                <li className="flex items-start">
                                    <span className="text-brand mr-2">•</span>
                                    Expert technical support and training
                                    available
                                </li>
                            </ul>
                        </div>
                    </AnimateIn>

                    <Link
                        href="/about"
                        className="inline-flex items-center justify-center bg-brand hover:bg-white hover:text-brand-dark text-white px-6 md:px-8 py-3 md:py-4 uppercase tracking-wide font-medium text-sm md:text-base border border-brand transition-colors duration-300 cursor-pointer active:scale-[0.97]"
                    >
                        Learn More About Us
                        <ArrowRight className="w-5 h-5 ml-2" />
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default About;
